import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Alert, Button, Card, Col, Form as BsForm, Row, Spinner } from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import { toast } from 'react-toastify';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import DateMask from '~/Components/DateMask';
import * as constants from '~/constant';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { exportarCotacoes, importarCotacoes } from '~/Services/CotacaoMoedaForteService';

const exportarSchema = Yup.object().shape({
  data_inicial: Yup.string()
    .required('Informe a data inicial')
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Data inicial inválida')
    .test('valid-date', 'Data inicial inválida', (value) =>
      value ? moment(value, 'DD/MM/YYYY', true).isValid() : false,
    ),
  data_final: Yup.string()
    .required('Informe a data final')
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Data final inválida')
    .test('valid-date', 'Data final inválida', (value) =>
      value ? moment(value, 'DD/MM/YYYY', true).isValid() : false,
    )
    .test('depois-da-inicial', 'Data final deve ser igual ou posterior à data inicial', function (value) {
      const { data_inicial } = this.parent;
      if (!value || !data_inicial) return true;
      const ini = moment(data_inicial, 'DD/MM/YYYY', true);
      const fim = moment(value, 'DD/MM/YYYY', true);
      if (!ini.isValid() || !fim.isValid()) return true;
      return fim.isSameOrAfter(ini);
    }),
});

const gerarNomeArquivo = () => {
  const hoje = new Date();
  const ddmmyy = hoje
    .toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    .replace(/\//g, '');
  return `Cota${ddmmyy}.Txt`;
};

const ExportacaoImportacaoCotacaoMoedaForte = () => {
  const [arquivo, setArquivo] = useState(null);
  const [loadingImportar, setLoadingImportar] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [showConfirmacao, setShowConfirmacao] = useState(false);

  const handleExportar = async (values, { setSubmitting }) => {
    try {
      const dataIni = moment(values.data_inicial, 'DD/MM/YYYY').format('YYYY-MM-DD');
      const dataFim = moment(values.data_final, 'DD/MM/YYYY').format('YYYY-MM-DD');
      const response = await exportarCotacoes(dataIni, dataFim);
      const blob = new Blob([response], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = gerarNomeArquivo();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Arquivo gerado com sucesso');
    } catch (err) {
      const mensagem =
        err && typeof err === 'object' && err.message
          ? err.message
          : 'Nenhuma cotação encontrada para o período';
      toast.error(mensagem);
    } finally {
      setSubmitting(false);
    }
  };

  const handleArquivoSelecionado = (e) => {
    setArquivo(e.target.files[0] || null);
    setResultado(null);
  };

  const handleImportarClick = () => {
    setShowConfirmacao(true);
  };

  const handleConfirmacao = async (confirmado) => {
    setShowConfirmacao(false);
    if (!confirmado) return;

    setLoadingImportar(true);
    try {
      const response = await importarCotacoes(arquivo);
      setResultado(response);
      toast.success('Importação concluída');
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao importar arquivo';
      toast.error(mensagem);
    } finally {
      setLoadingImportar(false);
    }
  };

  return (
    <div>
      <Header title="Exportação e Importação de Cotação Moeda Forte" />

      <div className="container-fluid py-3">
        <Row className="align-items-stretch">
          {/* Card Exportar */}
          <Col xs={12} md={6} className="mb-4 d-flex">
            <Card className="h-100 w-100">
              <Card.Header>
                <strong>Exportar</strong>
              </Card.Header>
              <Card.Body>
                <Formik
                  initialValues={{ data_inicial: '', data_final: '' }}
                  validationSchema={exportarSchema}
                  onSubmit={handleExportar}
                >
                  {({ isSubmitting, errors, touched, values, setFieldValue }) => (
                    <Form>
                      <Row>
                        <Col xs={12} sm={6}>
                          <DateMask
                            allowEmpty
                            onValidate={() => {}}
                          >
                            {() => (
                              <BsForm.Group controlId="data_inicial">
                                <small>Data inicial</small>
                                <MaskedInput
                                  name="data_inicial"
                                  type="tel"
                                  guide={false}
                                  className={`form-control ${
                                    errors.data_inicial && touched.data_inicial ? 'is-invalid' : ''
                                  }`}
                                  placeholder="Ex.: 01/01/2019"
                                  mask={constants.MASK_DATE}
                                  value={values.data_inicial}
                                  onChange={(e) => setFieldValue('data_inicial', e.target.value)}
                                />
                                <BsForm.Control.Feedback className="d-flex" type="invalid">
                                  {errors.data_inicial}
                                </BsForm.Control.Feedback>
                              </BsForm.Group>
                            )}
                          </DateMask>
                        </Col>

                        <Col xs={12} sm={6}>
                          <DateMask
                            allowEmpty
                            onValidate={() => {}}
                          >
                            {() => (
                              <BsForm.Group controlId="data_final">
                                <small>Data final</small>
                                <MaskedInput
                                  name="data_final"
                                  type="tel"
                                  guide={false}
                                  className={`form-control ${
                                    errors.data_final && touched.data_final ? 'is-invalid' : ''
                                  }`}
                                  placeholder="Ex.: 01/01/2019"
                                  mask={constants.MASK_DATE}
                                  value={values.data_final}
                                  onChange={(e) => setFieldValue('data_final', e.target.value)}
                                />
                                <BsForm.Control.Feedback className="d-flex" type="invalid">
                                  {errors.data_final}
                                </BsForm.Control.Feedback>
                              </BsForm.Group>
                            )}
                          </DateMask>
                        </Col>
                      </Row>

                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={isSubmitting}
                        className="mt-2"
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Gerando...
                          </>
                        ) : (
                          'Exportar'
                        )}
                      </Button>
                    </Form>
                  )}
                </Formik>
              </Card.Body>
            </Card>
          </Col>

          {/* Card Importar */}
          <Col xs={12} md={6} className="mb-4 d-flex">
            <Card className="h-100 w-100">
              <Card.Header>
                <strong>Importar</strong>
              </Card.Header>
              <Card.Body>
                <BsForm.Group className="mb-3">
                  <small>Arquivo TXT</small>
                  <BsForm.Control
                    type="file"
                    accept=".txt"
                    size="sm"
                    onChange={handleArquivoSelecionado}
                  />
                </BsForm.Group>

                <Button
                  variant="primary"
                  size="sm"
                  disabled={!arquivo || loadingImportar}
                  onClick={handleImportarClick}
                >
                  {loadingImportar ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Importando...
                    </>
                  ) : (
                    'Importar'
                  )}
                </Button>

                {resultado && (
                  <Alert variant="info" className="mt-3 mb-0">
                    <strong>{resultado.total_importadas}</strong> cotações importadas.{' '}
                    <strong>{resultado.total_ignoradas}</strong> linhas ignoradas
                    {resultado.ativos_nao_encontrados &&
                      resultado.ativos_nao_encontrados.length > 0 && (
                        <span>
                          {' '}(ativos não encontrados:{' '}
                          {resultado.ativos_nao_encontrados.join(', ')})
                        </span>
                      )}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <ModalConfirmacao
          show={showConfirmacao}
          onClose={handleConfirmacao}
          title="Confirmar Importação"
          message={`Deseja importar o arquivo "${arquivo ? arquivo.name : ''}"? Esta ação irá atualizar as cotações no sistema.`}
          variant="danger"
        />
      </div>
    </div>
  );
};

export default ExportacaoImportacaoCotacaoMoedaForte;
